import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Button, Space } from "antd";
import { CheckOutlined, CloseOutlined, PlusOutlined, UndoOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { formatLength } from "./format";

// Live measuring with the camera through WebXR (ARCore on Android in Chrome). The device
// finds surfaces and reports where the centre of the screen hits them, in metres; two such
// points give a distance. Typical accuracy is about 1 cm per metre and depends on the device
// and the light, so the photo tool and the tape stay the reference. iPhones and the app's web
// views do not offer WebXR; the button is only shown where a session can start.

/** True where an AR session can be started. */
export const useArSupported = () => {
  const [supported, setSupported] = useState(false);
  useEffect(() => {
    let alive = true;
    navigator.xr
      ?.isSessionSupported?.("immersive-ar")
      .then(ok => alive && setSupported(Boolean(ok)))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);
  return supported;
};

// 4×4 column-major matrix times [x, y, z, 1].
const transform = (m, [x, y, z]) => [
  m[0] * x + m[4] * y + m[8] * z + m[12],
  m[1] * x + m[5] * y + m[9] * z + m[13],
  m[2] * x + m[6] * y + m[10] * z + m[14],
  m[3] * x + m[7] * y + m[11] * z + m[15]
];

const distanceMm = (a, b) => Math.round(Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]) * 1000);

const ArMeasure = forwardRef(({ onUnsupported }, ref) => {
  const { t, i18n } = useTranslation("dashboard");
  const lng = i18n.language;
  const [active, setActive] = useState(false);
  const [points, setPoints] = useState([]);
  const [hasHit, setHasHit] = useState(false);
  const overlay = useRef(null);
  const canvas = useRef(null);
  const state = useRef({ session: null, hit: null, points: [], target: null });

  const stop = () => state.current.session?.end().catch(() => {});

  const addPoint = () => {
    const { hit } = state.current;
    if (!hit) return;
    const next = [...state.current.points, hit];
    state.current.points = next;
    setPoints(next);
  };

  const undo = () => {
    const next = state.current.points.slice(0, -1);
    state.current.points = next;
    setPoints(next);
  };

  const lastDistance =
    points.length >= 2 ? distanceMm(points[points.length - 2], points[points.length - 1]) : null;

  const accept = () => {
    if (lastDistance === null) return;
    state.current.target?.apply(lastDistance);
    stop();
  };

  const draw = (view, hit) => {
    const el = canvas.current;
    if (!el) return;
    const ratio = window.devicePixelRatio || 1;
    const width = window.innerWidth;
    const height = window.innerHeight;
    if (el.width !== width * ratio) {
      el.width = width * ratio;
      el.height = height * ratio;
    }
    const ctx = el.getContext("2d");
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.clearRect(0, 0, width, height);
    const toScreen = point => {
      const eye = transform(view.transform.inverse.matrix, point);
      const clip = transform(view.projectionMatrix, eye.slice(0, 3));
      if (clip[3] <= 0) return null;
      return [
        ((clip[0] / clip[3]) * 0.5 + 0.5) * width,
        (1 - ((clip[1] / clip[3]) * 0.5 + 0.5)) * height
      ];
    };
    const all = hit ? [...state.current.points, hit] : state.current.points;
    ctx.lineWidth = 3;
    ctx.strokeStyle = "#28bee5";
    ctx.fillStyle = "#ffffff";
    ctx.font = "600 16px system-ui, sans-serif";
    ctx.textAlign = "center";
    for (let i = 1; i < all.length; i += 1) {
      // The live segment from the last point to the crosshair is drawn dashed.
      const a = toScreen(all[i - 1]);
      const b = toScreen(all[i]);
      if (!a || !b) continue;
      ctx.setLineDash(hit && i === all.length - 1 ? [8, 6] : []);
      ctx.beginPath();
      ctx.moveTo(...a);
      ctx.lineTo(...b);
      ctx.stroke();
      const label = formatLength(distanceMm(all[i - 1], all[i]), lng);
      const mx = (a[0] + b[0]) / 2;
      const my = (a[1] + b[1]) / 2 - 10;
      ctx.lineWidth = 4;
      ctx.strokeStyle = "rgba(0,0,0,0.6)";
      ctx.setLineDash([]);
      ctx.strokeText(label, mx, my);
      ctx.fillText(label, mx, my);
      ctx.lineWidth = 3;
      ctx.strokeStyle = "#28bee5";
    }
    state.current.points.forEach(point => {
      const p = toScreen(point);
      if (!p) return;
      ctx.beginPath();
      ctx.arc(p[0], p[1], 6, 0, Math.PI * 2);
      ctx.fill();
    });
    // Crosshair in the middle: where the next point goes.
    ctx.setLineDash([]);
    ctx.strokeStyle = hit ? "#ffffff" : "rgba(255,255,255,0.4)";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(width / 2, height / 2, 14, 0, Math.PI * 2);
    ctx.moveTo(width / 2 - 22, height / 2);
    ctx.lineTo(width / 2 + 22, height / 2);
    ctx.moveTo(width / 2, height / 2 - 22);
    ctx.lineTo(width / 2, height / 2 + 22);
    ctx.stroke();
  };

  const start = async target => {
    if (!navigator.xr) {
      onUnsupported?.();
      return;
    }
    let session;
    try {
      // Must be the first await after the tap: browsers only start AR from a user gesture.
      session = await navigator.xr.requestSession("immersive-ar", {
        requiredFeatures: ["hit-test", "local"],
        optionalFeatures: ["dom-overlay"],
        domOverlay: { root: overlay.current }
      });
    } catch {
      onUnsupported?.();
      return;
    }
    state.current = { session, hit: null, points: [], target };
    setPoints([]);
    setActive(true);

    const gl = document.createElement("canvas").getContext("webgl", { xrCompatible: true });
    await gl.makeXRCompatible?.();

    session.updateRenderState({ baseLayer: new XRWebGLLayer(session, gl) });
    const local = await session.requestReferenceSpace("local");
    const viewer = await session.requestReferenceSpace("viewer");
    const hitSource = await session.requestHitTestSource({ space: viewer });

    // A tap on the camera image (not on a button) sets a point.
    session.addEventListener("select", addPoint);
    session.addEventListener("end", () => {
      hitSource.cancel?.();
      state.current.session = null;
      setActive(false);
      setHasHit(false);
    });

    const onFrame = (time, frame) => {
      if (!state.current.session) return;
      session.requestAnimationFrame(onFrame);
      const layer = session.renderState.baseLayer;
      gl.bindFramebuffer(gl.FRAMEBUFFER, layer.framebuffer);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      const pose = frame.getViewerPose(local);
      if (!pose) return;
      const hits = frame.getHitTestResults(hitSource);
      const position = hits[0]?.getPose(local)?.transform.position;
      state.current.hit = position ? [position.x, position.y, position.z] : null;
      setHasHit(Boolean(position));
      draw(pose.views[0], state.current.hit);
    };
    session.requestAnimationFrame(onFrame);
  };

  useImperativeHandle(ref, () => ({ start }));
  useEffect(() => stop, []);

  // Buttons must not also count as a tap on the camera image.
  useEffect(() => {
    const el = overlay.current?.querySelector(".ar-controls");
    if (!el) return undefined;
    const block = event => event.preventDefault();
    el.addEventListener("beforexrselect", block);
    return () => el.removeEventListener("beforexrselect", block);
  }, [active]);

  const target = state.current.target;
  return createPortal(
    <div ref={overlay} className={active ? "ar-overlay ar-overlay--on" : "ar-overlay"}>
      <canvas ref={canvas} className="ar-canvas" />
      {active && (
        <>
          <div className="ar-hint">
            {target?.label && <div className="ar-target">{target.label}</div>}
            {hasHit
              ? points.length
                ? t("Aim at the end point and tap +.")
                : t("Aim at the start point and tap +.")
              : t("Move the phone slowly until surfaces are found.")}
            {lastDistance !== null && (
              <div className="ar-distance">{formatLength(lastDistance, lng)}</div>
            )}
          </div>
          <div className="ar-controls">
            <Space wrap>
              <Button
                size="large"
                shape="circle"
                icon={<CloseOutlined />}
                aria-label={t("Close")}
                onClick={stop}
              />
              <Button
                size="large"
                shape="circle"
                icon={<UndoOutlined />}
                aria-label={t("Undo")}
                disabled={!points.length}
                onClick={undo}
              />
              <Button
                size="large"
                type="primary"
                shape="circle"
                icon={<PlusOutlined />}
                aria-label={t("Add point")}
                disabled={!hasHit}
                onClick={addPoint}
              />
              {target && (
                <Button
                  size="large"
                  type="primary"
                  icon={<CheckOutlined />}
                  disabled={lastDistance === null}
                  onClick={accept}
                >
                  {t("Use")}
                </Button>
              )}
            </Space>
          </div>
        </>
      )}
    </div>,
    document.body
  );
});

export default ArMeasure;
